import { useRef, useState, useEffect } from 'preact/hooks'

import Icon from '@/components/snippets/icon/icon'

import { SearchInput } from 'libs/searchspring/components/quick-search/SearchInput'
import { QuickSearchDropdown } from 'libs/searchspring/components/quick-search/QuickSearchDropdown'
import { SearchspringAutocompleteProvider } from 'libs/searchspring/fetchers/autocomplete'

function QuickSearch({ id = 'quick-search' }) {
  const searchInputRef = useRef()
  const [inputValue, setInputValue] = useState()
  const [query, setQuery] = useState()
  const closeButtonRef = useRef()
  const modalRef = useRef()
  const [redirect, setRedirect] = useState(false)

  const handleClose = (event) => {
    event.preventDefault()
    modalRef.current?.handleHide()
  }

  const handleUpdateValue = (event) => {
    setInputValue(event.target.value)
  }

  const resetInputValue = () => {
    setInputValue('')
    searchInputRef.current.value = ''
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(inputValue)
    }, 400)
    return () => {
      clearTimeout(timeout)
    }
  }, [inputValue])

  useEffect(() => {
    if (!modalRef.current || !searchInputRef.current) return

    const modal = modalRef.current
    const input = searchInputRef.current

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ui') {
          if (modal.dataset.ui === 'visible') {
            input?.focus()
          }
        }
      })
    })

    observer.observe(modal, {
      attributes: true,
      attributeFilter: ['data-ui']
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <x-modal className="group/search z-0 flex h-full items-center" data-id={id} data-body-lock ref={modalRef}>
      <div className="pointer-events-none fixed z-100 flex h-fit w-full flex-col opacity-0 transition-all inset-tl group-data-visible/search:pointer-events-auto group-data-visible/search:opacity-100">
        <form
          action="/search"
          method="get"
          className="h-[72px] w-full border-b border-neutral-medium bg-secondary lg:h-[106px]"
        >
          <div className="flex h-full items-stretch justify-between gap-x-1 lg:gap-x-1.5">
            <div className="flex grow items-center gap-x-0.75 pl-margin">
              <div className="h-[16px] w-[15px] children:size-full lg:h-[23px] lg:w-[22px]">
                <Icon id="magnifying-glass" />
              </div>
              <SearchInput ref={searchInputRef} redirect={redirect} onInput={handleUpdateValue} />
            </div>
            <div className="flex h-full shrink-0 items-stretch justify-end gap-x-1 lg:gap-x-4">
              {inputValue ? (
                <button
                  className="text-line text-b text-tertiary link-fade"
                  onClick={resetInputValue}
                  type="reset"
                  aria-label="Clear search"
                >
                  Clear<span className="lg-max:hidden"> Input</span>
                </button>
              ) : null}
              <button
                className="flex aspect-1/1 h-full shrink-0 items-center justify-center border-l border-neutral-medium link-fade"
                data-modal-parent={id}
                data-modal-role="close"
                ref={closeButtonRef}
                onClick={handleClose}
                aria-label="Close modal"
              >
                <div className="size-[11px] children:size-full">
                  <Icon id="close" />
                </div>
              </button>
            </div>
          </div>
        </form>
        <SearchspringAutocompleteProvider runOnMount={false} resultsPerPage={4}>
          <QuickSearchDropdown query={query} redirect={redirect} setRedirect={setRedirect} />
        </SearchspringAutocompleteProvider>
      </div>
      <button
        className="pointer-events-none fixed z-90 size-full cursor-n-resize bg-neutral-black/60 opacity-0 transition-all inset-tl group-data-visible/search:pointer-events-auto group-data-visible/search:opacity-100"
        data-modal-parent={id}
        data-modal-role="close"
        aria-label="Close modal"
      ></button>
    </x-modal>
  )
}

export function WrappedQuickSearch({ id }) {
  return (
    <SearchspringAutocompleteProvider runOnMount={false}>
      <QuickSearch id={id} />
    </SearchspringAutocompleteProvider>
  )
}
